import React, { useState, useRef, useEffect } from "react";
import { X, Shuffle, Eye, Download, Maximize2, ChevronDown, Palette } from "lucide-react";
import { useToast } from '../ToastNotifications';
import { applyColorBlindnessFilter } from './utils/colorUtils';
import { CopyButtons } from './components/CopyButtons';
import { TabButton } from './components/TabButton';
import { Tooltip } from './components/Tooltip';
import { tabs } from './config/TabConfig';
import PageHeader from '../PageHeader';
import BackToTop from '../BackToTop';
import './styles/scrollbar.css';

const Visualizer = ({
    isOpen,
    onClose,
    colors,
    onColorsChange,
    colorBlindnessType = 'normal',
    isDark = false,
    inline = false,
    savedPalettes = []
}) => {
    const [activeTab, setActiveTab] = useState("images");
    const [width, setWidth] = useState(600);
    const [isResizing, setIsResizing] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isPaletteDropdownOpen, setIsPaletteDropdownOpen] = useState(false);
    const resizeRef = useRef(null);
    const dropdownRef = useRef(null);
    const mobileScrollRef = useRef(null);
    const desktopScrollRef = useRef(null);

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState(null);

    // Function to expand panel to maximum width
    const expandToMaxWidth = () => {
        setWidth(1200); // Maximum width as defined in resize constraints
    };

    // Simple position-based color names
    const getColorRoleName = (index) => {
        return `Color ${index + 1}`;
    };

    const getColorRoleIcon = (index) => {
        // No icons needed - keeping function for potential future use
        return null;
    };

    const getColorRoleDescription = (index) => {
        return `Position ${index + 1} in palette`;
    };

    // Export design tokens with semantic names
    const exportDesignTokens = () => {
        let css = ':root {\n  /* Design System Color Tokens */\n';

        filteredColors.forEach((color, index) => {
            const roleName = getColorRoleName(index);
            const tokenName = `--color-${roleName.toLowerCase().replace(/\s+/g, '-')}`;
            css += `  ${tokenName}: ${color.value};\n`;
        });

        css += '\n  /* Usage Examples */\n';
        filteredColors.forEach((color, index) => {
            const roleName = getColorRoleName(index).toLowerCase().replace(/\s+/g, '-');
            css += `  /* .${roleName} { color: var(--color-${roleName}); } */\n`;
            css += `  /* .bg-${roleName} { background-color: var(--color-${roleName}); } */\n`;
        });

        css += '}';
        return css;
    };

    // Detect mobile on mount and window resize
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768; // Tailwind md breakpoint
            setIsMobile(mobile);

            // Set appropriate width for desktop
            if (!mobile && width < 600) {
                setWidth(600);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [width]);

    // Apply color blindness filter to colors
    const filteredColors = applyColorBlindnessFilter(colors, colorBlindnessType);

    const activeTabComponent = tabs.find(tab => tab.id === activeTab)?.component;

    const shuffleColors = () => {
        const shuffled = [...colors].sort(() => Math.random() - 0.5);
        onColorsChange(shuffled);
    };

    // Apply saved palette
    const applySavedPalette = (palette) => {
        const newColors = palette.colors.map((color, index) => ({
            id: `palette-color-${Date.now()}-${index}`,
            value: color
        }));
        onColorsChange(newColors);
        setIsPaletteDropdownOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsPaletteDropdownOpen(false);
            }
        };

        if (isPaletteDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPaletteDropdownOpen]);

    // Drag and drop handlers
    const handleDragStart = (e, index) => {
        setDraggedItem(colors[index]);
        e.dataTransfer.effectAllowed = "move";
        // Add a small timeout to ensure the drag image is set correctly
        setTimeout(() => {
            e.target.style.opacity = '0.5';
        }, 0);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDraggedOverIndex(index);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        // Only clear if we're leaving the container, not entering a child
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDraggedOverIndex(null);
        }
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        setDraggedOverIndex(null);

        if (draggedItem) {
            const dragIndex = colors.findIndex(color => color.id === draggedItem.id);
            if (dragIndex !== dropIndex) {
                const newColors = [...colors];
                const [removed] = newColors.splice(dragIndex, 1);
                newColors.splice(dropIndex, 0, removed);
                onColorsChange(newColors);
            }
        }
        setDraggedItem(null);
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedItem(null);
        setDraggedOverIndex(null);
    };

    // Resize functionality (desktop only)
    useEffect(() => {
        if (isMobile) return; // Skip resize functionality on mobile

        const handleMouseMove = (e) => {
            if (isResizing) {
                const newWidth = window.innerWidth - e.clientX;
                setWidth(Math.max(400, Math.min(1200, newWidth)));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, isMobile]);

    if (!isOpen) return null;

    // Inline page layout (for activity bar section)
    if (inline) {
        return (
            <div className="overflow-visible">
                <PageHeader
                    title="Visualizer"
                    description={
                        colorBlindnessType !== 'normal'
                            ? `Preview colors in UI designs (with ${colorBlindnessType} simulation)`
                            : 'Preview colors in UI designs'
                    }
                    tabs={tabs.map(tab => ({
                        id: tab.id,
                        label: tab.label
                    }))}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {/* Content */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">


                    {/* Color Palette with Role Names */}
                    <div className="px-4 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Design System Colors ({filteredColors.length})
                                </span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Drag to reassign color roles
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Saved Palettes Dropdown */}
                                {savedPalettes.length > 0 && (
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={() => setIsPaletteDropdownOpen(!isPaletteDropdownOpen)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-600"
                                        >
                                            <Palette className="w-3 h-3" />
                                            Load Palette
                                            <ChevronDown className={`w-3 h-3 transform transition-transform ${isPaletteDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {isPaletteDropdownOpen && (
                                            <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-64 overflow-y-auto">
                                                {savedPalettes.map((palette) => (
                                                    <button
                                                        key={palette.id}
                                                        onClick={() => applySavedPalette(palette)}
                                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 border-b border-gray-100 dark:border-gray-700 last:border-0"
                                                    >
                                                        <div className="font-medium text-gray-900 dark:text-gray-100">
                                                            {palette.name}
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {palette.colors.length} colors
                                                            </span>
                                                            <div className="flex gap-0.5">
                                                                {palette.colors.slice(0, 5).map((color, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        className="w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600"
                                                                        style={{ backgroundColor: color }}
                                                                    />
                                                                ))}
                                                                {palette.colors.length > 5 && (
                                                                    <span className="text-xs text-gray-400">+{palette.colors.length - 5}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={shuffleColors}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                >
                                    <Shuffle className="w-3 h-3" />
                                    Shuffle
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar-horizontal">
                            {filteredColors.map((color, index) => {
                                const roleName = getColorRoleName(index);
                                const roleDesc = getColorRoleDescription(index);
                                const isBeingDragged = draggedItem?.id === color.id;
                                const isDragTarget = draggedOverIndex === index && !isBeingDragged;

                                return (
                                    <Tooltip key={color.id} content={`${roleName} • ${roleDesc} • ${color.value}`}>
                                        <div className={`flex-shrink-0 text-center group transition-all duration-200 ${isDragTarget ? 'scale-110' : ''}`}>
                                            <div
                                                className={`relative w-12 h-12 rounded-xl border-2 shadow-md cursor-move transform transition-all duration-200 hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                                                    isDragTarget
                                                        ? 'border-blue-500 dark:border-blue-400 shadow-blue-500/50'
                                                        : 'border-white dark:border-gray-700 group-hover:border-gray-300 dark:group-hover:border-gray-500'
                                                }`}
                                                style={{
                                                    backgroundColor: color.value,
                                                    opacity: isBeingDragged ? 0.5 : 1,
                                                }}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, index)}
                                                onDragOver={(e) => handleDragOver(e, index)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, index)}
                                                onDragEnd={handleDragEnd}
                                                tabIndex="0"
                                            >
                                                <div className="absolute inset-1 rounded-lg shadow-inner opacity-20 pointer-events-none"></div>
                                            </div>
                                            <div className="w-16 mt-1.5">
                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 block truncate">
                                                    {roleName}
                                                </span>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6 bg-gray-50 dark:bg-gray-900">
                        {activeTabComponent && React.createElement(activeTabComponent, { colors: filteredColors })}
                    </div>

                    {/* Export Section */}
                    <div className="px-4 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            Export Current View
                            {colorBlindnessType !== 'normal' && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 ml-2">
                                    (with {colorBlindnessType} simulation)
                                </span>
                            )}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            <CopyButtons
                                getCss={() => `:root {\n${filteredColors.map((color, i) => `  --color-${getColorRoleName(i).toLowerCase().replace(/\s+/g, '-')}: ${color.value};`).join('\n')}\n}`}
                                getSvg={() => `<svg width="${filteredColors.length * 50}" height="50" xmlns="http://www.w3.org/2000/svg">\n${filteredColors.map((c, i) => `  <rect x="${i * 50}" y="0" width="50" height="50" fill="${c.value}" />`).join('\n')}\n</svg>`}
                                getPalette={() => filteredColors.map((c, i) => `${getColorRoleName(i)}: ${c.value}`).join('\n')}
                                title={`${activeTab}-palette${colorBlindnessType !== 'normal' ? '-' + colorBlindnessType : ''}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Mobile full-screen layout
    if (isMobile) {
        return (
            <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900">
                <div className="flex flex-col h-full">
                    {/* Mobile Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-3">
                            <h3 className="text-lg font-medium">Visualizer</h3>
                            {colorBlindnessType !== 'normal' && (
                                <div className="flex items-center gap-2 px-2 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                                    <Eye className="w-3 h-3" />
                                    <span className="capitalize">{colorBlindnessType}</span>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Mobile Tabs - Horizontal scroll */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        <div className="flex space-x-2 overflow-x-auto custom-scrollbar-horizontal pb-2">
                            {tabs.map((tab) => (
                                <TabButton
                                    key={tab.id}
                                    icon={tab.icon}
                                    label={tab.label}
                                    isActive={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Mobile Color Palette with Names */}
                    <div className="px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Design System Colors ({filteredColors.length})
                                </span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Drag to reassign color roles
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                {/* Mobile Saved Palettes Dropdown */}
                                {savedPalettes.length > 0 && (
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={() => setIsPaletteDropdownOpen(!isPaletteDropdownOpen)}
                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-600"
                                        >
                                            <Palette className="w-3 h-3" />
                                            <span className="hidden sm:inline">Load</span>
                                            <ChevronDown className={`w-3 h-3 transform transition-transform ${isPaletteDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {isPaletteDropdownOpen && (
                                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-48 overflow-y-auto">
                                                {savedPalettes.map((palette) => (
                                                    <button
                                                        key={palette.id}
                                                        onClick={() => applySavedPalette(palette)}
                                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 border-b border-gray-100 dark:border-gray-700 last:border-0"
                                                    >
                                                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                                            {palette.name}
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {palette.colors.length} colors
                                                            </span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={shuffleColors}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors duration-200"
                                >
                                    <Shuffle className="w-3 h-3" />
                                    <span className="hidden sm:inline">Shuffle</span>
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3 overflow-x-auto pb-2">
                            {filteredColors.map((color, index) => {
                                const roleName = getColorRoleName(index);
                                const roleDesc = getColorRoleDescription(index);
                                const isBeingDragged = draggedItem?.id === color.id;
                                const isDragTarget = draggedOverIndex === index && !isBeingDragged;

                                return (
                                    <Tooltip key={color.id} content={`${roleName} • ${roleDesc} • ${color.value}`}>
                                        <div className={`flex-shrink-0 text-center group transition-all duration-200 ${isDragTarget ? 'scale-110' : ''}`}>
                                            <div
                                                className={`relative w-10 h-10 rounded-xl border-2 shadow-md cursor-move transform transition-all duration-200 hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                                                    isDragTarget
                                                        ? 'border-blue-500 dark:border-blue-400 shadow-blue-500/50'
                                                        : 'border-white dark:border-gray-700 group-hover:border-gray-300 dark:group-hover:border-gray-500'
                                                }`}
                                                style={{
                                                    backgroundColor: color.value,
                                                    opacity: isBeingDragged ? 0.5 : 1,
                                                }}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, index)}
                                                onDragOver={(e) => handleDragOver(e, index)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, index)}
                                                onDragEnd={handleDragEnd}
                                                tabIndex="0"
                                            >
                                                {/* Subtle inner shadow for depth */}
                                                <div className="absolute inset-1 rounded-lg shadow-inner opacity-20 pointer-events-none"></div>
                                            </div>
                                            <div className="w-14 mt-1">
                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 block truncate">
                                                    {roleName}
                                                </span>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mobile Tab Content - Full remaining height */}
                    <div ref={mobileScrollRef} className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                        {activeTabComponent && React.createElement(activeTabComponent, { colors: filteredColors })}
                    </div>

                    {/* Mobile Export Section - Enhanced */}
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            Export Current View
                            {colorBlindnessType !== 'normal' && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 ml-2">
                                    (with {colorBlindnessType} simulation)
                                </span>
                            )}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            <CopyButtons
                                getCss={() => `:root {\n${filteredColors.map((color, i) => `  --color-${getColorRoleName(i).toLowerCase().replace(/\s+/g, '-')}: ${color.value};`).join('\n')}\n}`}
                                getSvg={() => `<svg width="${filteredColors.length * 50}" height="50" xmlns="http://www.w3.org/2000/svg">\n${filteredColors.map((c, i) => `  <rect x="${i * 50}" y="0" width="50" height="50" fill="${c.value}" />`).join('\n')}\n</svg>`}
                                getPalette={() => filteredColors.map((c, i) => `${getColorRoleName(i)}: ${c.value}`).join('\n')}
                                title={`${activeTab}-palette${colorBlindnessType !== 'normal' ? '-' + colorBlindnessType : ''}`}
                            />
                        </div>
                    </div>
                </div>

                {/* Back to Top Button for Mobile */}
                <BackToTop scrollContainerRef={mobileScrollRef} threshold={300} />
            </div>
        );
    }

    // Desktop sidebar layout
    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
            <div className="fixed inset-y-0 right-0 z-50 flex bg-white dark:bg-gray-900 shadow-2xl" style={{ width }}>
                {/* Desktop Resize Handle */}
                <div
                    ref={resizeRef}
                    onMouseDown={() => setIsResizing(true)}
                    className="w-1 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 dark:hover:bg-blue-400 cursor-col-resize transition-colors duration-200"
                />

                <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
                    {/* Desktop Header */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-3">
                            <h3 className="text-lg font-medium">Visualizer</h3>
                            {colorBlindnessType !== 'normal' && (
                                <div className="flex items-center gap-2 px-2 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                                    <Eye className="w-3 h-3" />
                                    <span className="capitalize">{colorBlindnessType}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Expand to Maximum Width Button */}
                            <div className="group relative">
                                <button
                                    onClick={expandToMaxWidth}
                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                                    disabled={width >= 1200}
                                >
                                    <Maximize2 className={`h-5 w-5 ${width >= 1200 ? 'text-gray-400' : ''}`} />
                                </button>

                                {/* Tooltip positioned below the button */}
                                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                    <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                                        Expand to maximum width
                                        {/* Arrow pointing up */}
                                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-100 rotate-45"></div>
                                    </div>
                                </div>
                            </div>
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Desktop Tabs */}
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        <div className="flex space-x-1 overflow-x-auto custom-scrollbar-horizontal">
                            {tabs.map((tab) => (
                                <TabButton
                                    key={tab.id}
                                    icon={tab.icon}
                                    label={tab.label}
                                    isActive={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Desktop Color Palette with Role Names */}
                    <div className="px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Design System Colors ({filteredColors.length})
                                </span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Drag to reassign color roles
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Desktop Saved Palettes Dropdown */}
                                {savedPalettes.length > 0 && (
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={() => setIsPaletteDropdownOpen(!isPaletteDropdownOpen)}
                                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-600"
                                        >
                                            <Palette className="w-3 h-3" />
                                            Load
                                            <ChevronDown className={`w-3 h-3 transform transition-transform ${isPaletteDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {isPaletteDropdownOpen && (
                                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-48 overflow-y-auto">
                                                {savedPalettes.map((palette) => (
                                                    <button
                                                        key={palette.id}
                                                        onClick={() => applySavedPalette(palette)}
                                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 border-b border-gray-100 dark:border-gray-700 last:border-0"
                                                    >
                                                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                                            {palette.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            {palette.colors.length} colors
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={shuffleColors}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors duration-200"
                                >
                                    <Shuffle className="w-3 h-3" />
                                    Shuffle
                                </button>
                            </div>
                        </div>

                        {/* Compact horizontal layout with role names */}
                        <div className="flex gap-3 overflow-x-auto pb-1 custom-scrollbar-horizontal">
                            {filteredColors.map((color, index) => {
                                const roleName = getColorRoleName(index);
                                const roleDesc = getColorRoleDescription(index);
                                const isBeingDragged = draggedItem?.id === color.id;
                                const isDragTarget = draggedOverIndex === index && !isBeingDragged;

                                return (
                                    <Tooltip key={color.id} content={`${roleName} • ${roleDesc} • ${color.value}`}>
                                        <div className={`flex-shrink-0 group mt-2 transition-all duration-200 ${isDragTarget ? 'scale-110' : ''}`}>
                                            <div
                                                className={`relative w-10 h-10 rounded-xl border-2 shadow-md cursor-move transform transition-all duration-200 hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                                                    isDragTarget
                                                        ? 'border-blue-500 dark:border-blue-400 shadow-blue-500/50'
                                                        : 'border-white dark:border-gray-700 group-hover:border-gray-300 dark:group-hover:border-gray-500'
                                                }`}
                                                style={{
                                                    backgroundColor: color.value,
                                                    opacity: isBeingDragged ? 0.5 : 1,
                                                }}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, index)}
                                                onDragOver={(e) => handleDragOver(e, index)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, index)}
                                                onDragEnd={handleDragEnd}
                                                tabIndex="0"
                                            >
                                                {/* Subtle inner shadow for depth */}
                                                <div className="absolute inset-1 rounded-lg shadow-inner opacity-20 pointer-events-none"></div>
                                            </div>
                                            <div className="w-14 mt-1">
                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 block truncate">
                                                    {roleName}
                                                </span>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>

                    {/* Desktop Tab content */}
                    <div ref={desktopScrollRef} className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                        {activeTabComponent && React.createElement(activeTabComponent, { colors: filteredColors })}
                    </div>

                    {/* Desktop Export Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            Export Current View
                            {colorBlindnessType !== 'normal' && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 ml-2">
                                    (with {colorBlindnessType} simulation)
                                </span>
                            )}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            <CopyButtons
                                getCss={() => `:root {\n${filteredColors.map((color, i) => `  --color-${getColorRoleName(i).toLowerCase().replace(/\s+/g, '-')}: ${color.value};`).join('\n')}\n}`}
                                getSvg={() => `<svg width="${filteredColors.length * 50}" height="50" xmlns="http://www.w3.org/2000/svg">\n${filteredColors.map((c, i) => `  <rect x="${i * 50}" y="0" width="50" height="50" fill="${c.value}" />`).join('\n')}\n</svg>`}
                                getPalette={() => filteredColors.map((c, i) => `${getColorRoleName(i)}: ${c.value}`).join('\n')}
                                title={`${activeTab}-palette${colorBlindnessType !== 'normal' ? '-' + colorBlindnessType : ''}`}
                            />
                        </div>
                    </div>

                    {/* Back to Top Button for Desktop */}
                    <BackToTop scrollContainerRef={desktopScrollRef} threshold={300} />
                </div>
            </div>
        </>
    );
};

export default Visualizer;